import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PREMIUM_BUDGET_STEP_ID } from '@/features/guided-financial-planning/config';
import { useGuidedPlanningContext } from '@/features/guided-financial-planning/context';

/**
 * Hook to sync react-use-wizard with react-router.
 * Returns startIndex and onStepChange props for the Wizard component.
 */
export function useWizardRouter() {
  const { flatStepConfigs } = useGuidedPlanningContext();
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = location.pathname.split('/');
  const currentStepPath = pathSegments[pathSegments.length - 1] ?? '';

  const startIndex = useMemo(() => {
    const rawIndex = flatStepConfigs.findIndex(step => step.path === currentStepPath);
    if (rawIndex >= 0) {
      return rawIndex;
    }
    if (
      currentStepPath === PREMIUM_BUDGET_STEP_ID &&
      !flatStepConfigs.some(s => s.id === PREMIUM_BUDGET_STEP_ID)
    ) {
      const quotesLoading = flatStepConfigs.findIndex(s => s.id === 'quotes-loading');
      if (quotesLoading >= 0) {
        return quotesLoading;
      }
      const optionsIdx = flatStepConfigs.findIndex(s => s.id === 'options');
      return optionsIdx >= 0 ? optionsIdx : 0;
    }
    return 0;
  }, [currentStepPath, flatStepConfigs]);

  useEffect(() => {
    const segments = location.pathname.split('/');
    const last = segments[segments.length - 1] ?? '';
    if (
      last === PREMIUM_BUDGET_STEP_ID &&
      !flatStepConfigs.some(s => s.id === PREMIUM_BUDGET_STEP_ID)
    ) {
      const redirect = flatStepConfigs.find(s => s.id === 'quotes-loading');
      if (redirect) {
        const newPath = [...segments.slice(0, -1), redirect.path].join('/');
        navigate({ pathname: newPath, search: location.search }, { replace: true });
      }
    }
  }, [flatStepConfigs, location.pathname, location.search, navigate]);

  const onStepChange = useCallback(
    (stepIndex: number) => {
      const stepPath = flatStepConfigs[stepIndex]?.path;
      if (!stepPath) return;

      const segments = location.pathname.split('/');
      const lastSegment = segments[segments.length - 1];
      const isValidStep = flatStepConfigs.some(s => s.path === lastSegment);

      const newPath = isValidStep
        ? [...segments.slice(0, -1), stepPath].join('/')
        : `${location.pathname}/${stepPath}`;

      const currentStepConfig = flatStepConfigs.find(s => s.path === lastSegment);
      const shouldReplace = currentStepConfig?.skipHistory ?? false;

      navigate({ pathname: newPath, search: location.search }, { replace: shouldReplace });
    },
    [flatStepConfigs, location.pathname, location.search, navigate],
  );

  return { startIndex, onStepChange };
}
